import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Icon, Dropdown, Chip } from '@column/column-ui-kit';
import { ReportingRepository } from '~/repositories';
import {
  EmptyState,
  PaginationWrapper,
  PaginationWrapperRefProps,
  PopoverFilterEntry,
  NotificationList,
  TableColumn,
} from '~/components';
import { ROUTE } from '~/app/routes';
import { EditPage, Headline } from '~/styles';
import { formatString, isValidDate } from '~/util';

const Arrow = styled(Icon.ArrowRight)`
  display: inline-block;
  vertical-align: top;
  margin: 4px;

  --icon-size: 16px;
  --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
`;

export const PageReporting: React.FC = () => {
  const navigate = useNavigate();
  const paginationRef = useRef<PaginationWrapperRefProps>(null);

  const handleFetch = async (params: any) => {
    return ReportingRepository.getAll(params).then((response) => {
      const entries: any = [];
      if (response.reports.length) {
        response.reports.map((entry: any) => {
          entries.push({
            id: entry.id,
            date: (
              <>
                {entry.fromDate} <Arrow /> {entry.toDate}
              </>
            ),
            type: entry.type,
            status: entry.status,
            created: entry.createdAt,
            action: (
              <Dropdown
                isInside
                positionRight
                options={[
                  {
                    label: 'View',
                    onClick: () => navigate(`${ROUTE.REPORTING}/view/${entry.id}`),
                  },
                ]}
                variant="dots"
              />
            ),
          });
        });
      }
      return { entries, hasMore: response.hasMore };
    });
  };

  const columns: TableColumn[] = [
    {
      Header: 'Date',
      accessor: 'date',
      percent: 27,
    },
    {
      Header: 'Type',
      Cell: (current) => <Chip type="default">{formatString(current.value)}</Chip>,
      accessor: 'type',
      percent: 24,
    },
    {
      Header: 'Status',
      Cell: (current) => (
        <Chip type={current.value === 'completed' ? 'success' : current.value === 'failed' ? 'danger' : 'default'}>
          {formatString(current.value)}
        </Chip>
      ),
      accessor: 'status',
    },
    {
      Header: 'Created',
      Cell: (current) => (isValidDate(current.value) ? current.value.toLocaleDateString('en-US') : '-'),
      accessor: 'created',
      sortType: 'datetime',
    },
    {
      Header: '',
      accessor: 'action',
    },
  ];

  const filter: PopoverFilterEntry[] = [
    {
      id: 'type',
      label: 'Type',
      type: 'select',
      list: [
        {
          value: 'bank_account_transaction',
          label: 'Bank account transaction',
        },
        {
          value: 'bank_account_summary',
          label: 'Bank account summary',
        },
      ],
    },
    {
      id: 'fromDate',
      label: 'From (Date)',
      type: 'exactDate',
    },
    {
      id: 'toDate',
      label: 'To (Date)',
      type: 'exactDate',
    },
  ];

  return (
    <EditPage fullWidth smallGap>
      <Headline>Reporting</Headline>

      <div>
        <NotificationList display="page" />
        <PaginationWrapper
          tableId="reporting"
          ref={paginationRef}
          fetch={handleFetch}
          columns={columns}
          filter={filter}
          empty={
            <EmptyState headline="No reports found" text="Schedule your first report to get started.">
              <Button onClick={() => navigate(`${ROUTE.REPORTING}/schedule`)} size="small" icon={<Icon.CircleAdd />}>
                Create Report
              </Button>
            </EmptyState>
          }
          action={
            <Button onClick={() => navigate(`${ROUTE.REPORTING}/schedule`)} icon={<Icon.CircleAdd />} size="small">
              Create Report
            </Button>
          }
          rowClick={(row) => {
            navigate(`${ROUTE.REPORTING}/view/${row.original.id}`);
          }}
        />
      </div>
    </EditPage>
  );
};
